import * as React from 'react';

import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    type AdditionalServicesPriceType,
    useDisplayedAdditionalServicesFormValues,
} from 'src/features/parkingManagement/providers/AdditionalServicesProvider';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { CURRENCIES } from 'src/features/payStations/types';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { formatPrice } from 'src/features/reservations/utils/utils';
import PriceFormField from 'src/registry/components/forms/PriceFormField';
import { DialogTitle } from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';

const AdditionalServicesPricing = () => {
    const { control, setValue, getValues } = useFormContext();
    const { formatMessage } = useIntl();

    const { displayedFormValues, setDisplayedFormValues } = useDisplayedAdditionalServicesFormValues();

    const { tax_percent, price_with_tax } = getValues();
    const calculatedPrice = React.useMemo(() => {
        const tax = Number(tax_percent);

        if (!price_with_tax || isNaN(tax) || tax === 0) return 0;
        return Math.round(price_with_tax / (1 + tax));
    }, [price_with_tax, tax_percent]);

    const taxAmountCalculation = React.useMemo(() => {
        const tax = Number(tax_percent);

        if (!calculatedPrice || isNaN(tax) || tax === 0) return 0;
        return (calculatedPrice * tax) / 100 || 0;
    }, [calculatedPrice, tax_percent]);

    React.useEffect(() => {
        const isValidSourceData = price_with_tax > 0 && Number(tax_percent) > 0;
        if (!isValidSourceData) return;

        const roundedPrice = Math.round(calculatedPrice);
        const roundedTaxAmount = Math.round(Number(taxAmountCalculation) * 100);

        setDisplayedFormValues((prev) => ({
            ...prev,
            tax_amount: taxAmountCalculation.toFixed(2),
            price: formatPrice(roundedPrice),
        }));

        setValue('tax_amount', roundedTaxAmount, {
            shouldValidate: true,
            shouldDirty: true,
        });
        setValue('price', roundedPrice, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [taxAmountCalculation, calculatedPrice]);

    return (
        <>
            <DialogTitle>
                <FormattedMessage {...reservationMessages.pricing} />
            </DialogTitle>
            <Separator className="mb-4" />

            <PriceFormField<AdditionalServicesPriceType>
                value={displayedFormValues['price_with_tax']}
                fieldName="price_with_tax"
                label={formatMessage(reservationMessages.priceWithVat)}
                isRequired={true}
                setDisplayedFormValues={setDisplayedFormValues}
            />
            <PriceFormField<AdditionalServicesPriceType>
                value={displayedFormValues['tax_percent']}
                fieldName="tax_percent"
                label={`${formatMessage(reservationMessages.vat)} %`}
                isRequired={true}
                setDisplayedFormValues={setDisplayedFormValues}
            />
            <PriceFormField<AdditionalServicesPriceType>
                value={taxAmountCalculation === 0 ? '0.00' : displayedFormValues['tax_amount']}
                fieldName="tax_amount"
                label={formatMessage(reservationMessages.vatAmount)}
                disabled
                setDisplayedFormValues={setDisplayedFormValues}
            />
            <PriceFormField<AdditionalServicesPriceType>
                value={calculatedPrice === 0 ? '0.00' : displayedFormValues['price']}
                fieldName="price"
                label={formatMessage(reservationMessages.priceWithoutVat)}
                disabled
                setDisplayedFormValues={setDisplayedFormValues}
            />

            <FormField
                control={control}
                name="currency"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...parkingManagementMessages.currency} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(val: CURRENCIES) => field.onChange(val)}
                                    value={(field.value as CURRENCIES) ?? ''}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(reservationMessages.selectCurrency)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(CURRENCIES).map(([key, value]) => (
                                            <SelectItem key={key} value={value}>
                                                {value}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </>
    );
};

export default AdditionalServicesPricing;
